/**
 * 路径路由（SEO 友好）。
 *
 * 从旧的哈希路由（#paipan）改为真实路径（/paipan）：
 * - 每个页面有独立可收录的 URL
 * - 配合预渲染，百度/搜索引擎能抓到每页内容
 * - nginx 已有 SPA fallback（try_files … /index.html），直接访问 /paipan 也能进
 *
 * 路由 key 保持不变（home / paipan / legal/about …），只是承载方式从 hash 变成 pathname，
 * 所以各页面组件、renderRoute 都无需改动。
 *
 * SSR 安全：typeof window 判断，供预渲染时在 Node 里渲染指定路由。
 */
import { useEffect, useState, type MouseEvent } from "react";
import { trackPageview } from "./api";

export const navItems = [
  { key: "home", label: "首页" },
  { key: "paipan", label: "四柱排盘" },
  { key: "records", label: "记录案例" },
  { key: "famous", label: "名人案例" },
  { key: "hepan", label: "双盘对照" },
  { key: "study", label: "古籍课堂" },
  { key: "vip", label: "会员中心" },
] as const;

export const AUTH_ROUTE = "auth";
export const DEFAULT_ROUTE = "home";

/** 路由 key → URL 路径（用于 <a href>，让爬虫可跟随、用户可直达/分享）。 */
export function routeToPath(route: string): string {
  return route === DEFAULT_ROUTE ? "/" : `/${route}`;
}

/**
 * 供 <a href> 使用的点击处理：普通左键走 SPA 内部跳转，
 * 修饰键点击（Cmd/Ctrl/新标签页）/中键保持浏览器默认行为（真链接的好处）。
 */
export function onNavClick(
  event: MouseEvent<HTMLAnchorElement>,
  route: string,
  navigate: (next: string) => void,
): void {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }
  event.preventDefault();
  navigate(route);
}

function pathToRoute(pathname: string): string {
  const trimmed = pathname.replace(/^\/+/, "").replace(/\/+$/, "");
  return trimmed || DEFAULT_ROUTE;
}

function readRoute(): string {
  if (typeof window === "undefined") {
    // 预渲染时由脚本注入目标路由
    return (globalThis as { __ROUTE__?: string }).__ROUTE__ || DEFAULT_ROUTE;
  }
  return pathToRoute(window.location.pathname);
}

/**
 * 微信扫码回调检测。微信把 ?code=&state= 拼在路径后，
 * 靠 query 里同时出现 code+state 识别，需在常规路由之前拦截。
 */
export function isWechatCallback(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.has("code") && params.has("state");
}

export function useRoute(): [string, (next: string) => void] {
  const [route, setRoute] = useState(readRoute);

  useEffect(() => {
    const onPop = () => setRoute(readRoute());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    // 初次加载 + 每次 SPA 跳转都在此触发，正好作为 PV 打点点位。
    // 后台自己的页面不计入访问统计，避免管理员日常操作污染 PV 数据。
    if (route !== "admin") trackPageview(route);
  }, [route]);

  const navigate = (next: string) => {
    const path = routeToPath(next);
    if (path !== window.location.pathname) {
      window.history.pushState(null, "", path);
    }
    setRoute(next);
  };

  return [route, navigate];
}
